import { useState, useMemo, useEffect } from 'react';
import { Button, Chip } from '@progress/kendo-react-buttons';
import { Input } from '@progress/kendo-react-inputs';
import { DrawerItemProps } from '@progress/kendo-react-layout';
import { useLocalization } from '@progress/kendo-react-intl';
import { enMessages } from '../../../../messages/en-US';
import { UnscheduleClickPayload } from './WorkOrdersTypes';
import { useAccountSettingsStore } from '../../../../Zustand/accountSettingsStore';
import { GetHeadUnitIcon } from '../../../../Utilities/CustomIcons';

export type WorkOrdersUnschedulePanelItemProps = DrawerItemProps &
  UnscheduleClickPayload & { onClose: () => void };

export function WorkOrdersUnschedulePanelItem({
  assetName,
  assetId,
  scheduleName,
  unscheduleEvents,
  onClose,
}: WorkOrdersUnschedulePanelItemProps) {
  const localizationService = useLocalization();
  const timezone = useAccountSettingsStore((state) => state.accountTimezone);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    setSearchQuery('');
  }, [unscheduleEvents]);

  const allPoints = useMemo(
    () => unscheduleEvents.flatMap((g) => g.events.map((e) => ({ occurred: g.occurred, ...e }))),
    [unscheduleEvents]
  );

  const visiblePoints = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return q ? allPoints.filter((p) => p.pointName.toLowerCase().includes(q)) : allPoints;
  }, [allPoints, searchQuery]);

  const occurredDate = unscheduleEvents[0]?.occurred
    ? new Date(unscheduleEvents[0].occurred).toLocaleDateString(undefined, { timeZone: timezone })
    : '';

  const assetDisplay = assetName ?? assetId ?? '—';
  const scheduleDisplay = scheduleName?.trim() || '—';

  const unscheduledL = localizationService.toLanguageString(
    'custom.workOrdersLegendUnscheduled',
    enMessages.custom.workOrdersLegendUnscheduled
  );
  const occurredL = localizationService.toLanguageString(
    'custom.workOrdersUnscheduledOccurredLabel',
    enMessages.custom.workOrdersUnscheduledOccurredLabel
  );
  const eventsL = localizationService.toLanguageString('custom.events', enMessages.custom.events);
  const volumeL = localizationService.toLanguageString(
    'custom.volumeDelivered',
    enMessages.custom.volumeDelivered
  );
  const searchPlaceholderL = localizationService.toLanguageString(
    'custom.workOrdersDetailSearchPoints',
    enMessages.custom.workOrdersDetailSearchPoints
  );
  const noResultsL = localizationService.toLanguageString(
    'custom.workOrdersDetailNoSearchResults',
    enMessages.custom.workOrdersDetailNoSearchResults
  );

  return (
    <div className="flex h-screen max-h-screen min-h-0 min-w-0 flex-col overflow-hidden bg-white">
      <div className="shrink-0 px-3 py-2">
        <div className="flex items-start justify-between gap-2">
          <div
            className="min-w-0 flex-1 truncate text-base font-semibold leading-snug text-slate-900"
            title={assetDisplay}
          >
            {assetDisplay}
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <Chip text={unscheduledL} themeColor="error" className="!shrink-0" />
            <Button
              fillMode="flat"
              themeColor="base"
              className="!shrink-0"
              onClick={onClose}
              aria-label="Close panel"
            >
              ✕
            </Button>
          </div>
        </div>

        <div className="mt-1 truncate text-sm font-medium text-slate-800" title={scheduleDisplay}>
          {scheduleDisplay}
        </div>

        <div className="mt-2 flex flex-col gap-1">
          {occurredDate && (
            <span className="inline-flex items-baseline gap-x-1.5">
              <span className="text-[11px] uppercase leading-none tracking-wide text-slate-400">
                {occurredL}
              </span>
              <span className="text-sm font-medium leading-tight tabular-nums text-slate-900">
                {occurredDate}
              </span>
            </span>
          )}
          <span className="inline-flex items-baseline gap-x-1.5">
            <span className="text-[11px] uppercase leading-none tracking-wide text-slate-400">
              {eventsL}
            </span>
            <span className="text-sm font-medium leading-tight tabular-nums text-slate-900">
              {allPoints.length}
            </span>
          </span>
        </div>
      </div>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <div className="shrink-0 px-3 py-2">
          <div className="relative">
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.value)}
              placeholder={searchPlaceholderL}
              className="w-full"
            />
            {searchQuery && (
              <Button
                fillMode="flat"
                themeColor="base"
                onClick={() => setSearchQuery('')}
                className="!absolute !right-1 !top-1/2 !-translate-y-1/2 !p-0.5 !text-slate-400 hover:!text-slate-600"
                aria-label="Clear search"
              >
                ✕
              </Button>
            )}
          </div>
        </div>

        {visiblePoints.length === 0 ? (
          <div className="flex min-h-0 flex-1 flex-col justify-center px-4 py-6 text-center text-sm text-slate-500">
            {noResultsL}
          </div>
        ) : (
          <ul className="flex min-h-0 flex-1 flex-col overflow-y-auto">
            {visiblePoints.map((point, i) => (
              <li key={i} className="border-b border-slate-100 px-4 py-3 last:border-b-0">
                <div className="break-words text-sm font-medium text-slate-800">{point.pointName}</div>
                <div className="mt-1.5 flex flex-col gap-y-0.5">
                  {point.occurred && (
                    <span className="inline-flex items-baseline gap-x-1">
                      <span className="text-sm font-medium tabular-nums text-slate-700">
                        {new Date(point.occurred).toLocaleDateString(undefined, { timeZone: timezone })}
                        {' '}
                        {new Date(point.occurred).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: timezone })}
                      </span>
                    </span>
                  )}
                  {point.rfid && (
                    <span className="inline-flex items-baseline gap-x-1">
                      <span className="text-sm text-slate-400">RFID</span>
                      <span className="text-sm font-medium tabular-nums text-slate-700">{point.rfid}</span>
                    </span>
                  )}
                  <div className="flex items-center justify-between gap-x-2">
                    <span className="inline-flex items-baseline gap-x-1">
                      <span className="text-sm text-slate-400">{volumeL}</span>
                      <span className="text-sm font-medium tabular-nums text-slate-700">
                        {point.volumeDelivered != null ? `${Number(point.volumeDelivered).toFixed(2)} cc` : '—'}
                      </span>
                    </span>
                    <span className="inline-flex shrink-0 items-center gap-x-1 text-slate-700">
                      <span className="text-slate-400">{GetHeadUnitIcon(24, 24)}</span>
                      <span className="text-sm font-medium">{point.deviceName?.trim() || '—'}</span>
                    </span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
